import { ReactNode } from 'react';
import { SymphonyMark } from '../../SymphonyMark';
import styles from './SignupShell.module.css';

/**
 * Symphony's shell with nothing in it — an empty white rail carrying the brand mark, and the grey field
 * beside it — for the seconds `/welcome` spends creating a recipient.
 *
 * Built here rather than by reusing `AppFrame` or `BrandHeader`, and the reason is the whole point of this
 * screen: the caller has NO tenant yet. `/welcome` is in `CHROME_FREE_ROUTES` precisely so that mounting
 * the app's chrome does not fire a burst of tenant-scoped calls that can only fail against a metasite that
 * does not exist — duplexer 409, personality 401, site-language 404 (#7713). `BrandHeader` alone would
 * bring `useUnreadNotifications`, which fetches on mount, so even the brand row cannot be borrowed whole.
 *
 * What that leaves is the rail's geometry duplicated in one place: 260px, the mark at top 24 / inline-start
 * 11, sized 30. Those numbers belong to `AppFrame`'s rail and `BrandHeader`'s brand row, and nothing makes
 * them move together — a rail resize would leave this behind. It is a deliberate trade for a screen that
 * must make no calls at all, and the drift is cosmetic and brief where the calls were neither.
 */
export function SignupShell({ children }: { children: ReactNode }) {
  return (
    <div className={styles.shell}>
      <div className={styles.rail}>
        <div className={styles.brand}>
          <SymphonyMark size={30} />
          <span className={styles.wordmark}>Symphony</span>
        </div>
      </div>
      <main className={styles.field}>{children}</main>
    </div>
  );
}
